import React from 'react';
import { Image, ImageStyle, ImageURISource } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { SharedStyles } from '@shared/styles';
import { MBImageProps } from './props';

const REACT_FORWARD_REF_TYPE = Symbol.for('react.forward_ref');
const REACT_MEMO_TYPE = Symbol.for('react.memo');

type AnyProps = Record<string, unknown>;
type ComponentLike = React.ElementType<AnyProps>;

function isComponentLike(value: unknown): value is ComponentLike {
  if (typeof value === 'function') {
    return true;
  }
  if (value && typeof value === 'object') {
    const maybe = value as { $$typeof?: symbol };
    return maybe.$$typeof === REACT_FORWARD_REF_TYPE || maybe.$$typeof === REACT_MEMO_TYPE;
  }
  return false;
}

function getDefaultExport(value: unknown): unknown {
  if (value && typeof value === 'object' && 'default' in (value as Record<string, unknown>)) {
    return (value as Record<string, unknown>).default;
  }
  return undefined;
}

function resolveComponentFromSource(source: unknown): ComponentLike | null {
  const direct = isComponentLike(source) ? source : undefined;
  const fromDefault = direct ? undefined : getDefaultExport(source);
  const candidate = direct ?? fromDefault;
  return isComponentLike(candidate) ? candidate : null;
}

export function MBImage({ fill, style, source, tintColor, ...otherProps }: MBImageProps) {
  // If `source` is a React component or a module with a default component (SVGR)
  const Content = resolveComponentFromSource(source);
  if (Content) {
    return (
      <MBView style={style} fill={fill}>
        <Content style={{ width: '100%', height: '100%' }} color={tintColor} />
      </MBView>
    );
  }

  // This default return looks the same as in index.js
  return (
    <Image
      style={[fill && SharedStyles.fill, style as ImageStyle]}
      source={source as ImageURISource}
      {...otherProps}
    />
  );
}
